import { useAuth0 } from '@auth0/auth0-react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import FuelConfirm from '@/components/fuel-confirm/fuel-confirm';
import { VNConfirmModal } from '@/modals/VNConfirmModal';
import type { OU } from '@/models/Ou';
import type { Permissions } from '@/models/Permissions';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import useAppUtils from '@/utils/appUtils';

export const AppResourcesContext = createContext({
  permissions: null as Permissions,
  ouData: null as OU,
});
export const useAppResources = () => useContext(AppResourcesContext);

type Props = {
  children: React.ReactNode;
};

const AppResourcesProvider = ({ children }: Props) => {
  const { user, isLoading } = useAuth0();

  const { getAppResources } = useAppUtils();
  // const { user } = useSelector(userSelector);
  const { ouData, confirmMessage, confirmMessageValues } = useSelector(applicationDataSelector);
  // TODO eventually get rid of selector
  const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const [loading, setLoading] = useState(true);

  const init = async () => {
    if (!ouData) {
      await getAppResources(fuelUserAccessData, user, permissions, ouData);
    }

    setLoading(false);
  };

  useEffect(() => {
    // wait for authentication
    if (isLoading) return;

    init();
  }, [isLoading]);

  const value = useMemo(
    () => ({
      permissions,
      ouData,
    }),
    [permissions, ouData]
  );

  return (
    <>
      <FuelConfirm value={confirmMessage} />
      {confirmMessageValues && <VNConfirmModal {...confirmMessageValues} />}
      {loading ? null : <AppResourcesContext.Provider value={value}>{children}</AppResourcesContext.Provider>}
    </>
  );
};

export default AppResourcesProvider;
